import { useState } from "react"

import { parseErrorAsObj } from "../../../../errors/errorProcessors"
import { IAppError } from "../../../../errors/errorTypes"
import { useAppDispatch } from "../../../../hooks/useDispatch"
import { useGetUserData } from "../../../../hooks/useGetUserData"
import { setShowAccountSetUp } from "../../../../redux/preferencesSlice"
import { SUPPRESSED } from "../../../../redux/types"
import { forceTokenRefresh } from "../../../../utils/auth/authUtils"
import { patchClientProtected } from "../../clients/clients"
import { CONFIRM_SET_UP } from "../../urls/private"

export const INSTALLER_SOFTWARE_PROVIDER = "INSTALLER_SOFTWARE_PROVIDER"
export const SINGLE_INSTALLER = "SINGLE_INSTALLER"
export const MANUAL_SET_UP = "MANUAL_SET_UP"

export type TConfirmSetUpMode =
    | typeof INSTALLER_SOFTWARE_PROVIDER
    | typeof SINGLE_INSTALLER
    | typeof MANUAL_SET_UP

interface IConfirmSetUp {
    isConfirming: boolean
    error: IAppError | undefined
    patchConfirmSetUp: () => Promise<void>
}

/**
 * Hook for calling the confirm set up API endpoint.
 * @returns {IConfirmSetUp} - loading and error states and the function to call the API.
 */
const useConfirmSetUp = (mode: TConfirmSetUpMode): IConfirmSetUp => {
    const dispatch = useAppDispatch()
    const { onSignOut } = useGetUserData()

    const [isConfirming, setIsConfirming] = useState(false)
    const [error, setError] = useState<IAppError | undefined>(undefined)

    const patchConfirmSetUp = async (): Promise<void> => {
        try {
            setIsConfirming(true)
            await patchClientProtected(CONFIRM_SET_UP(mode), null)
            dispatch(setShowAccountSetUp(SUPPRESSED))
            if (mode === SINGLE_INSTALLER) {
                forceTokenRefresh()
            } else if (mode === INSTALLER_SOFTWARE_PROVIDER) {
                onSignOut()
            } else {
                onSignOut()
            }
        } catch (err) {
            const errorObj = parseErrorAsObj(err)
            setError(errorObj)
        } finally {
            setIsConfirming(false)
        }
    }

    return {
        error,
        isConfirming,
        patchConfirmSetUp,
    }
}

export default useConfirmSetUp
